import { useRouter, useRoute, type LocationQuery } from "vue-router";

const PASS_THROUGH_QUERY_KEYS = ["impersonateOrg"] as const;

/**
 * The only query params that follow the user across pages (sidebar, logo,
 * profile). Page-local state such as `?tab=` stays on its own route.
 */
export const passThroughQuery = (query: LocationQuery): LocationQuery => {
  const kept: LocationQuery = {};
  for (const key of PASS_THROUGH_QUERY_KEYS) {
    const value = query[key];
    if (value !== undefined) kept[key] = value;
  }
  return kept;
};

export function useRouterNavigation() {
  const router = useRouter();
  const route = useRoute();

  /**
   * Navigate while preserving existing query parameters
   * @param to - Route location
   * @param preserveQuery - Whether to preserve current query params (default: true)
   */
  const navigateWithQuery = (
    to: {
      name: string;
      params?: Record<string, any>;
      query?: Record<string, any>;
    },
    preserveQuery = true
  ) => {
    const routeObject = {
      name: to.name,
      params: to.params ?? {},
      query: preserveQuery ? { ...route.query, ...to.query } : to.query ?? {},
    };

    return router.push(routeObject);
  };

  /**
   * Helper to get route object with preserved query params
   * @param routeName - Route name
   * @param params - Route params
   * @param additionalQuery - Additional query params to add/override
   */
  const getRouteWithQuery = (
    routeName: string,
    params?: Record<string, any>,
    additionalQuery?: Record<string, any>
  ) => {
    return {
      name: routeName,
      params: params ?? {},
      query: { ...route.query, ...additionalQuery },
    };
  };

  return {
    navigateWithQuery,
    getRouteWithQuery,
  };
}
